import { Add } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import { GridColDef, GridRowId, useGridApiRef } from '@mui/x-data-grid-pro';
import { useCallback } from 'react';
import { useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { v4 } from 'uuid';

import { useDemandRequestGridConfig } from '../../DemandRequestWidget.edit.dataGrid';
import { DemandRequestFormData } from '../DemandRequestDetailsForm/DemandRequestDetailsForm.schema';
import { I18nNamespace } from '~/common/types';
import { DataGridProStyled } from '~/components/DataGridCustom';
import { Action, Target, useTrackInteraction } from '~/hooks/useTrackInteraction/useTrackInteraction';

export const DemandRequestDetailsLineItemsForm = () => {
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'DemandRequestWidget' });
  const track = useTrackInteraction();
  const { fields, remove, append } = useFieldArray<DemandRequestFormData, 'demandRequest.lineItems'>({
    name: 'demandRequest.lineItems',
    shouldUnregister: true,
  });
  const apiRef = useGridApiRef();

  const getRowIndex = useCallback(
    (rowId: GridRowId) => {
      const rowIds = apiRef.current.getAllRowIds();
      const rowIndex = rowIds.findIndex((rId) => rId === rowId);
      return rowIndex;
    },
    [apiRef],
  );

  const handleRowDelete = useCallback(
    (id: GridRowId) => {
      // update react-hook-form
      const rowIndex = fields.findIndex((row) => row.id === id);

      remove(rowIndex);

      // update Data Grid cache
      apiRef.current.updateRows([{ id, _action: 'delete' }]);
      track(Target.DATA_GRID_ROW, Action.DELETED, { widget: 'demandRequest' });
    },
    [apiRef, fields, remove, track],
  );

  const config = useDemandRequestGridConfig({ getRowIndex, onRowDelete: handleRowDelete });
  const { columnVisibilityModel } = config;

  const handleAddRow = (row: any) => {
    append(row);
    track(Target.DATA_GRID_ROW, Action.ADDED, { widget: 'demandRequest' });
  };

  return (
    <>
      <DataGridProStyled
        {...config}
        columns={config.columns as unknown as GridColDef<(typeof fields)[number]>[]}
        apiRef={apiRef}
        autoHeight
        hideFooter
        disableColumnMenu
        rows={fields}
        getRowId={(row) => row.id}
        initialState={{
          pinnedColumns: { right: ['deleteAction'] },
        }}
        sx={{
          '& .MuiDataGrid-row': { cursor: 'default' },
        }}
        columnVisibilityModel={columnVisibilityModel}
        rowSelection={false}
        sortingMode={'server'}
      />
      <Box
        sx={{
          mt: 3,
        }}
      >
        <Button
          variant="outlined"
          onClick={() => {
            handleAddRow({
              id: v4(),
              position: `${fields.length + 1}`,
              quantity: 0,
              title: '',
              description: '',
              unitId: null,
              shipmentTerms: {
                earlyDeliveryAt: null,
                lateDeliveryAt: null,
              },
              preferredSupplierId: null,
              analysisId: null,
              expectedPricePerUnit: null,
              internalId: null,
              commodityGroupId: null,
              organizationId: null,
              deliveryLocationId: null,
              contractItemId: null,
              baselinePricePerUnit: null,
              commodityId: null,
            } as any);
          }}
          startIcon={<Add />}
          data-intercom-target="DemandRequest/LineItems/Add"
        >
          {t('addLineItemButton')}
        </Button>
      </Box>
    </>
  );
};
